import { useEffect, useState } from 'react'
import {
  desfazerAcao,
  dispensarAcao,
  SEGUNDOS_PARA_DESFAZER,
  useAcoesAgendadas,
  type AcaoAgendada,
} from '../envio/fila-de-acoes-em-massa'
import { juntar } from './ui'

/**
 * O aviso das ações em massa esperando os 15 segundos.
 *
 * Fica no pé da tela, fora da barra de seleção, porque a barra some quando a
 * seleção é limpa — e é justamente depois disso que a pessoa precisa ver o
 * relógio e o "Desfazer". Montado no layout, ele acompanha quem troca de
 * página no meio da contagem.
 */
export function AcoesAgendadas() {
  const acoes = useAcoesAgendadas()
  const [agora, setAgora] = useState(() => Date.now())
  const contando = acoes.some((a) => a.estado === 'aguardando')

  // O número na tela anda de segundo em segundo só enquanto há contagem.
  useEffect(() => {
    if (!contando) return
    const relogio = window.setInterval(() => setAgora(Date.now()), 250)
    return () => window.clearInterval(relogio)
  }, [contando])

  if (acoes.length === 0) return null

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4"
      aria-live="polite"
    >
      {acoes.map((acao) => (
        <AvisoDaAcao key={acao.id} acao={acao} agora={agora} />
      ))}
    </div>
  )
}

function AvisoDaAcao({ acao, agora }: { acao: AcaoAgendada; agora: number }) {
  const faltaMs = Math.max(0, acao.aconteceEm - agora)
  const segundos = Math.ceil(faltaMs / 1000)

  return (
    <div
      className={juntar(
        'aviso-entrando pointer-events-auto w-full max-w-md overflow-hidden rounded-xl shadow-suave ring-1',
        acao.estado === 'falhou'
          ? 'bg-red-50 ring-red-200'
          : acao.estado === 'feita'
            ? 'bg-green-50 ring-green-200'
            : 'bg-fundo-escuro ring-fundo-escuro',
      )}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p
            className={juntar(
              'text-sm font-medium',
              acao.estado === 'falhou'
                ? 'text-red-800'
                : acao.estado === 'feita'
                  ? 'text-green-800'
                  : 'text-white',
            )}
          >
            {acao.estado === 'feita' ? `Feito: ${acao.resumo}` : acao.resumo}
          </p>
          <p
            className={juntar(
              'text-xs',
              acao.estado === 'falhou'
                ? 'text-red-700'
                : acao.estado === 'feita'
                  ? 'text-green-700'
                  : 'text-white/70',
            )}
          >
            {acao.estado === 'aguardando'
              ? `Acontece em ${segundos} s`
              : acao.estado === 'executando'
                ? 'Aplicando…'
                : acao.estado === 'feita'
                  ? 'Os leads já foram atualizados.'
                  : acao.erro}
          </p>
        </div>

        {acao.estado === 'aguardando' ? (
          <button
            type="button"
            onClick={() => desfazerAcao(acao.id)}
            className="shrink-0 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-fundo-escuro transition hover:bg-white/85"
          >
            Desfazer
          </button>
        ) : acao.estado === 'falhou' ? (
          <button
            type="button"
            onClick={() => dispensarAcao(acao.id)}
            className="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium text-red-800 transition hover:bg-red-100"
          >
            Fechar
          </button>
        ) : null}
      </div>

      {/*
        A barra encolhe junto com o tempo que falta. Calculada do relógio, e
        não de uma animação solta: quem troca de página no meio da contagem vê
        a barra no ponto certo, e não recomeçando do cheio.
      */}
      {acao.estado === 'aguardando' ? (
        <div className="h-1 bg-white/15">
          <div
            className="h-full bg-white/70 transition-[width] duration-200 ease-linear"
            style={{ width: `${(faltaMs / (SEGUNDOS_PARA_DESFAZER * 1000)) * 100}%` }}
          />
        </div>
      ) : null}
    </div>
  )
}
